import RequestStatus from "../model/RequestStatus";
import {
    GetFeeTypeNamesResponse,
    GetFeeTypeExampleResponse,
    GetFeeTypeExampleVersionsResponse,
    SaveFeeTypeExampleResponse,
    DeleteFeeTypeExampleResponse,
    FeeTypeExampleUIState
} from "src/model/FeeTypeExample";
import {
    FETCH_FEE_TYPE_NAMES_START,
    FETCH_FEE_TYPE_NAMES_SUCCESS,
    FETCH_FEE_TYPE_NAMES_FAILED,
    FetchFeeTypeNamesCompleteAction,
    FETCH_FEE_TYPE_EXAMPLE_START,
    FETCH_FEE_TYPE_EXAMPLE_SUCCESS,
    FETCH_FEE_TYPE_EXAMPLE_FAILED,
    FetchFeeTypeExampleCompleteAction,
    SAVE_FEE_TYPE_EXAMPLE_START,
    SAVE_FEE_TYPE_EXAMPLE_SUCCESS,
    SAVE_FEE_TYPE_EXAMPLE_FAILED,
    SaveFeeTypeExampleCompleteAction,
    CREATE_FEE_TYPE_EXAMPLE_START,
    CREATE_FEE_TYPE_EXAMPLE_SUCCESS,
    CREATE_FEE_TYPE_EXAMPLE_FAILED,
    CreateFeeTypeExampleCompleteAction,
    DELETE_FEE_TYPE_EXAMPLE_START,
    DELETE_FEE_TYPE_EXAMPLE_SUCCESS,
    DELETE_FEE_TYPE_EXAMPLE_FAILED,
    DeleteFeeTypeExampleCompleteAction,
    FETCH_FEE_TYPE_EXAMPLE_VERSIONS_START,
    FETCH_FEE_TYPE_EXAMPLE_VERSIONS_SUCCESS,
    FETCH_FEE_TYPE_EXAMPLE_VERSIONS_FAILED,
    FetchFeeTypeExampleVersionsCompleteAction,
    ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_START,
    ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_SUCCESS,
    ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_FAILED,
    ActivateFeeTypeExampleVersionCompleteAction,
    SET_SELECTED_FEE_TYPE,
    SHOW_FEE_TYPE_MODAL,
    HIDE_FEE_TYPE_MODAL,
    SetSelectedFeeTypeAction,
    ShowFeeTypeModalAction,
    HideFeeTypeModalAction
} from "src/actions/FeeTypeExampleActions";

// Fee Type Names Reducer
const feeTypeNamesInitialState: GetFeeTypeNamesResponse = {
    requestStatus: RequestStatus.NOT_LOADED,
    data: null,
};

export const FeeTypeNamesReducer = (
    state: GetFeeTypeNamesResponse = feeTypeNamesInitialState,
    action: FetchFeeTypeNamesCompleteAction
): GetFeeTypeNamesResponse => {
    switch (action.type) {
        case FETCH_FEE_TYPE_NAMES_START:
            return {
                requestStatus: RequestStatus.LOADING,
                data: null,
            };
        case FETCH_FEE_TYPE_NAMES_SUCCESS:
            return {
                requestStatus: RequestStatus.SUCCESS,
                data: action.data,
            };
        case FETCH_FEE_TYPE_NAMES_FAILED:
            return {
                requestStatus: RequestStatus.ERROR,
                data: null,
            };
        default:
            return state;
    }
};

// UI State Reducer
const FeeTypeExampleUIInitialState: FeeTypeExampleUIState = {
    selectedFeeType: '',
    currentModal: null,
};

export const FeeTypeExampleUIReducer = (
    state: FeeTypeExampleUIState = FeeTypeExampleUIInitialState,
    action: SetSelectedFeeTypeAction | ShowFeeTypeModalAction | HideFeeTypeModalAction
): FeeTypeExampleUIState => {
    switch (action.type) {
        case SET_SELECTED_FEE_TYPE:
            return {
                ...state,
                selectedFeeType: (action as SetSelectedFeeTypeAction).selectedFeeType,
                currentModal: null, // Close any modal when switching fee types
            };
        case SHOW_FEE_TYPE_MODAL:
            return {
                ...state,
                currentModal: (action as ShowFeeTypeModalAction).modalType,
            };
        case HIDE_FEE_TYPE_MODAL:
            return {
                ...state,
                currentModal: null,
            };
        default:
            return state;
    }
};

// Fee Type Example Query Reducer
const FeeTypeExampleQueryInitialState: GetFeeTypeExampleResponse = {
    requestStatus: RequestStatus.NOT_LOADED,
    data: null,
};

export const FeeTypeExampleQueryReducer = (
    state: GetFeeTypeExampleResponse = FeeTypeExampleQueryInitialState,
    action: FetchFeeTypeExampleCompleteAction
): GetFeeTypeExampleResponse => {
    switch (action.type) {
        case FETCH_FEE_TYPE_EXAMPLE_START:
            return {
                requestStatus: RequestStatus.LOADING,
                data: null,
            };
        case FETCH_FEE_TYPE_EXAMPLE_SUCCESS:
            return {
                requestStatus: RequestStatus.SUCCESS,
                data: action.data,
            };
        case FETCH_FEE_TYPE_EXAMPLE_FAILED:
            return {
                requestStatus: RequestStatus.ERROR,
                data: null,
            };
        default:
            return state;
    }
};

// Fee Type Example Save Reducer
const FeeTypeExampleSaveInitialState: SaveFeeTypeExampleResponse = {
    requestStatus: RequestStatus.NOT_LOADED,
    data: null,
};

export const FeeTypeExampleSaveReducer = (
    state: SaveFeeTypeExampleResponse = FeeTypeExampleSaveInitialState,
    action: SaveFeeTypeExampleCompleteAction | 
           CreateFeeTypeExampleCompleteAction | 
           DeleteFeeTypeExampleCompleteAction | 
           ActivateFeeTypeExampleVersionCompleteAction
): SaveFeeTypeExampleResponse => {
    switch (action.type) {
        case SAVE_FEE_TYPE_EXAMPLE_START:
            return {
                requestStatus: RequestStatus.UPDATING,
                data: null,
            };
        case SAVE_FEE_TYPE_EXAMPLE_SUCCESS:
            return {
                requestStatus: RequestStatus.SUCCESS,
                data: (action as SaveFeeTypeExampleCompleteAction).data,
            };
        case SAVE_FEE_TYPE_EXAMPLE_FAILED:
            return {
                requestStatus: RequestStatus.ERROR,
                data: null,
                error: (action as SaveFeeTypeExampleCompleteAction).error,
            };
        // Clear save status when other operations start
        case CREATE_FEE_TYPE_EXAMPLE_START:
        case DELETE_FEE_TYPE_EXAMPLE_START:
        case ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_START:
            return {
                requestStatus: RequestStatus.NOT_LOADED,
                data: null,
            };
        default:
            return state;
    }
};

// Fee Type Example Create Reducer
const FeeTypeExampleCreateInitialState: SaveFeeTypeExampleResponse = {
    requestStatus: RequestStatus.NOT_LOADED,
    data: null,
};

export const FeeTypeExampleCreateReducer = (
    state: SaveFeeTypeExampleResponse = FeeTypeExampleCreateInitialState,
    action: CreateFeeTypeExampleCompleteAction | 
           SaveFeeTypeExampleCompleteAction | 
           DeleteFeeTypeExampleCompleteAction | 
           ActivateFeeTypeExampleVersionCompleteAction
): SaveFeeTypeExampleResponse => {
    switch (action.type) {
        case CREATE_FEE_TYPE_EXAMPLE_START:
            return {
                requestStatus: RequestStatus.UPDATING,
                data: null,
            };
        case CREATE_FEE_TYPE_EXAMPLE_SUCCESS:
            return {
                requestStatus: RequestStatus.SUCCESS,
                data: (action as CreateFeeTypeExampleCompleteAction).data,
            };
        case CREATE_FEE_TYPE_EXAMPLE_FAILED:
            return {
                requestStatus: RequestStatus.ERROR,
                data: null,
                error: (action as CreateFeeTypeExampleCompleteAction).error,
            };
        // Clear create status when other operations start
        case SAVE_FEE_TYPE_EXAMPLE_START:
        case DELETE_FEE_TYPE_EXAMPLE_START:
        case ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_START:
            return {
                requestStatus: RequestStatus.NOT_LOADED,
                data: null,
            };
        default:
            return state;
    }
};

// Fee Type Example Delete Reducer
const FeeTypeExampleDeleteInitialState: DeleteFeeTypeExampleResponse = {
    requestStatus: RequestStatus.NOT_LOADED,
};

export const FeeTypeExampleDeleteReducer = (
    state: DeleteFeeTypeExampleResponse = FeeTypeExampleDeleteInitialState,
    action: DeleteFeeTypeExampleCompleteAction | 
           SaveFeeTypeExampleCompleteAction | 
           CreateFeeTypeExampleCompleteAction | 
           ActivateFeeTypeExampleVersionCompleteAction
): DeleteFeeTypeExampleResponse => {
    switch (action.type) {
        case DELETE_FEE_TYPE_EXAMPLE_START:
            return {
                requestStatus: RequestStatus.UPDATING,
            };
        case DELETE_FEE_TYPE_EXAMPLE_SUCCESS:
            return {
                requestStatus: RequestStatus.SUCCESS,
            };
        case DELETE_FEE_TYPE_EXAMPLE_FAILED:
            return {
                requestStatus: RequestStatus.ERROR,
                error: (action as DeleteFeeTypeExampleCompleteAction).error,
            };
        // Clear delete status when other operations start
        case SAVE_FEE_TYPE_EXAMPLE_START:
        case CREATE_FEE_TYPE_EXAMPLE_START:
        case ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_START:
            return {
                requestStatus: RequestStatus.NOT_LOADED,
            };
        default:
            return state;
    }
};

// Fee Type Example Versions Reducer
const FeeTypeExampleVersionsInitialState: GetFeeTypeExampleVersionsResponse = {
    requestStatus: RequestStatus.NOT_LOADED,
    data: null,
};

export const FeeTypeExampleVersionsReducer = (
    state: GetFeeTypeExampleVersionsResponse = FeeTypeExampleVersionsInitialState,
    action: FetchFeeTypeExampleVersionsCompleteAction
): GetFeeTypeExampleVersionsResponse => {
    switch (action.type) {
        case FETCH_FEE_TYPE_EXAMPLE_VERSIONS_START:
            return {
                requestStatus: RequestStatus.LOADING,
                data: null,
            };
        case FETCH_FEE_TYPE_EXAMPLE_VERSIONS_SUCCESS:
            return {
                requestStatus: RequestStatus.SUCCESS,
                data: action.data,
            };
        case FETCH_FEE_TYPE_EXAMPLE_VERSIONS_FAILED:
            return {
                requestStatus: RequestStatus.ERROR,
                data: null,
            };
        default:
            return state;
    }
};

// Activate Version Reducer
const ActivateVersionInitialState: SaveFeeTypeExampleResponse = {
    requestStatus: RequestStatus.NOT_LOADED,
    data: null,
};

export const ActivateVersionReducer = (
    state: SaveFeeTypeExampleResponse = ActivateVersionInitialState,
    action: ActivateFeeTypeExampleVersionCompleteAction | 
           SaveFeeTypeExampleCompleteAction | 
           CreateFeeTypeExampleCompleteAction | 
           DeleteFeeTypeExampleCompleteAction
): SaveFeeTypeExampleResponse => {
    switch (action.type) {
        case ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_START:
            return {
                requestStatus: RequestStatus.UPDATING,
                data: null,
            };
        case ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_SUCCESS:
            return {
                requestStatus: RequestStatus.SUCCESS,
                data: (action as ActivateFeeTypeExampleVersionCompleteAction).data,
            };
        case ACTIVATE_FEE_TYPE_EXAMPLE_VERSION_FAILED:
            return {
                requestStatus: RequestStatus.ERROR,
                data: null,
                error: (action as ActivateFeeTypeExampleVersionCompleteAction).error,
            };
        // Clear activate status when other operations start
        case SAVE_FEE_TYPE_EXAMPLE_START:
        case CREATE_FEE_TYPE_EXAMPLE_START:
        case DELETE_FEE_TYPE_EXAMPLE_START:
            return {
                requestStatus: RequestStatus.NOT_LOADED,
                data: null,
            };
        default:
            return state;
    }
};
